// Set-typed bookkeeping fields stored directly on card instances.
// JSON.stringify silently flattens a Set to `{}` (no enumerable own
// properties), destroying both the type and the contents. So every
// path that round-trips a card through JSON (solo saves, P2P place
// broadcasts, auth snapshots) needs an explicit array form on the way
// out and a rehydration step on the way in. Reads like
// `_abilitiesUsedThisTurn.has(...)` / `.clear()` otherwise throw on a
// restored card and brick the turn-phase chain (v1.105 save-load bug).

export const CARD_SET_FIELDS = [
  // Once-per-turn ability gate (MARK_USED_THIS_TURN op /
  // not_used_this_turn condition). Cleared at the controller's
  // turn end by _stepPruneExpiredEffects.
  '_abilitiesUsedThisTurn',
  // Once-per-game ability gate. Never cleared, survives zone
  // changes (resetCardOnLeavePlay deliberately keeps it).
  '_abilitiesUsedThisGame',
  // Per-aura ward grant tracking (ops/wards.js).
  '_wardGrantedFromAuras',
];

/**
 * Return a JSON-safe view of `card` with every Set field converted to
 * an array. Copy-on-write: returns `card` itself when no Set fields
 * are present, a shallow copy otherwise. Never mutates the input.
 */
export function serializeCardSets(card) {
  if (!card) return card;
  let out = card;
  for (const field of CARD_SET_FIELDS) {
    const value = card[field];
    if (value instanceof Set) {
      if (out === card) out = { ...card };
      out[field] = Array.from(value);
    }
  }
  return out;
}

/**
 * Normalize the Set fields on `card` in place so downstream readers
 * can always call Set methods. Arrays (the serialized form) rehydrate
 * with their contents; anything else non-Set (the `{}` left behind by
 * a pre-fix save, or a sync path that stringified the Set raw) becomes
 * an empty Set. The original contents are unrecoverable by then.
 * Absent fields stay absent. Returns `card`.
 */
export function rehydrateCardSets(card) {
  if (!card) return card;
  for (const field of CARD_SET_FIELDS) {
    if (!(field in card)) continue;
    const value = card[field];
    if (value instanceof Set) continue;
    card[field] = new Set(Array.isArray(value) ? value : []);
  }
  return card;
}
